import type { Appointment, PatientSummary } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useClinical } from "../api";
import { errorText } from "../lib/errors";
import { WEEKDAY_LABELS, formatCalendarDate, formatTime, fullName, hhmm, isoWeekday, kindLabel, localDate, localMinutes, minutesOf, shiftDate, statusLabel, todayIn, zonedToIso } from "../lib/format";
import { useSession } from "../session";

type View = "day" | "week" | "month";
const KINDS = ["test", "retest", "continuation", "office_visit", "adjustment"] as const;

/** The first day (Monday) of the week holding `date`. */
const weekStart = (date: string) => shiftDate(date, 1 - isoWeekday(date));
const monthStart = (date: string) => `${date.slice(0, 7)}-01`;
const monthEnd = (date: string) => {
  const [y, m] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m!, 1)).toISOString().slice(0, 10);
};

/** The schedule over the location's hours and slots (Q-18): day, week and month views over searchAppointments; create, reschedule, cancel. */
export function CalendarPage() {
  const s = useSession();
  const api = useClinical();
  const navigate = useNavigate();
  const location = s.location;
  const timezone = location?.timezone ?? "America/Denver";
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState(() => todayIn(timezone));
  const [items, setItems] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const range = useMemo(() => {
    if (view === "day") return { from: anchor, to: shiftDate(anchor, 1) };
    if (view === "week") return { from: weekStart(anchor), to: shiftDate(weekStart(anchor), 7) };
    return { from: monthStart(anchor), to: monthEnd(anchor) };
  }, [view, anchor]);

  useEffect(() => {
    if (!location) return;
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      const all: Appointment[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 12; page++) {
        const res = await api.appointments.search({ from: zonedToIso(range.from, 0, timezone), to: zonedToIso(range.to, 0, timezone), locationId: location.locationId, limit: 50, cursor });
        all.push(...res.items);
        if (!res.nextCursor) break;
        cursor = res.nextCursor;
      }
      return all;
    })()
      .then((all) => live && setItems(all))
      .catch((err: unknown) => live && setError(errorText(err)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [api, location, range.from, range.to, timezone, tick]);

  const reload = () => setTick((t) => t + 1);
  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = range.from; d < range.to; d = shiftDate(d, 1)) out.push(d);
    return out;
  }, [range]);
  const byDay = useMemo(() => {
    const m = new Map<string, Appointment[]>();
    for (const a of items) {
      const d = localDate(a.scheduledAt, timezone);
      m.set(d, [...(m.get(d) ?? []), a]);
    }
    for (const list of m.values()) list.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    return m;
  }, [items, timezone]);

  async function act(id: string, fn: () => Promise<unknown>, done?: () => void) {
    setBusy(id);
    setNote(null);
    try {
      await fn();
      done?.();
      reload();
    } catch (err) {
      setNote(errorText(err));
    } finally {
      setBusy(null);
    }
  }
  const step = view === "day" ? 1 : view === "week" ? 7 : 0;
  const move = (dir: 1 | -1) => setAnchor(step ? shiftDate(anchor, dir * step) : dir > 0 ? monthEnd(anchor) : shiftDate(monthStart(anchor), -1));

  if (!location) return <Status kind="info" label="Your membership covers no location at this practice." />;
  const title = view === "day" ? formatCalendarDate(anchor) : view === "week" ? `Week of ${formatCalendarDate(weekStart(anchor))}` : new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${monthStart(anchor)}T00:00:00Z`));

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="mb-2 text-h1 font-semibold text-anchor">Calendar</h1>
          <p className="text-ink-secondary">
            {location.name} · {title} · {timezone}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(["day", "week", "month"] as View[]).map((v) => (
            <Button key={v} variant={view === v ? "primary" : "secondary"} onClick={() => setView(v)}>
              {v[0]!.toUpperCase() + v.slice(1)}
            </Button>
          ))}
          <Button variant="secondary" onClick={() => move(-1)}>
            Previous
          </Button>
          <Button variant="secondary" onClick={() => setAnchor(todayIn(timezone))}>
            Today
          </Button>
          <Button variant="secondary" onClick={() => move(1)}>
            Next
          </Button>
          {s.can("clinical:write") && <Button onClick={() => setCreating(view === "day" ? anchor : todayIn(timezone))}>New appointment</Button>}
        </div>
      </div>
      {error && <Status kind="error" label={`Couldn't load the schedule: ${error}`} className="mb-4 block" />}
      {note && <Status kind="error" label={note} className="mb-4 block" />}
      {creating && <AppointmentForm date={creating} onClose={() => setCreating(null)} onSaved={reload} />}
      {editing && <RescheduleForm appointment={editing} onClose={() => setEditing(null)} onSaved={reload} />}

      {view === "month" ? (
        <div className="grid grid-cols-7 gap-2">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="text-small font-medium text-ink-secondary">
              {w}
            </div>
          ))}
          {Array.from({ length: isoWeekday(range.from) - 1 }).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          {days.map((d) => (
            <button key={d} type="button" onClick={() => { setAnchor(d); setView("day"); }} className={`min-h-20 rounded-md border p-2 text-left ${d === todayIn(timezone) ? "border-accent-outline" : "border-rule-subtle"} ${location.schedule.closedWeekdays.includes(isoWeekday(d)) ? "bg-card-tinted" : "bg-card hover:bg-card-tinted"}`}>
              <span className="text-small text-ink-secondary">{Number(d.slice(8))}</span>
              {(byDay.get(d)?.length ?? 0) > 0 && <span className="block text-body font-medium">{byDay.get(d)!.filter((a) => a.status !== "cancelled").length} appt.</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className={`grid gap-4 ${view === "week" ? "md:grid-cols-7" : ""}`}>
          {days.map((d) => (
            <Card key={d} dense className={location.schedule.closedWeekdays.includes(isoWeekday(d)) ? "opacity-80" : undefined}>
              <h2 className="mb-2 text-small font-medium text-ink-secondary">
                {WEEKDAY_LABELS[isoWeekday(d) - 1]} {formatCalendarDate(d)}
                {location.schedule.closedWeekdays.includes(isoWeekday(d)) && " · closed"}
              </h2>
              {loading && items.length === 0 && <p className="text-small text-ink-secondary">Loading…</p>}
              {(byDay.get(d) ?? []).length === 0 && !loading && <p className="text-small text-ink-secondary">Nothing scheduled.</p>}
              <ul className="flex flex-col gap-2">
                {(byDay.get(d) ?? []).map((a) => (
                  <li key={a.appointmentId} className={`rounded-sm border border-rule-subtle p-2 ${a.status === "cancelled" ? "text-ink-secondary line-through" : ""}`}>
                    <p className="text-body">
                      <span className="tabular-nums">{formatTime(a.scheduledAt, timezone)}</span> · {kindLabel(a.kind)}
                    </p>
                    <p>
                      <Link to={`/patients/${a.patientId}`}>{fullName(a.patient)}</Link>
                    </p>
                    <p className="text-small text-ink-secondary">
                      {statusLabel(a.status)} · {a.durationMinutes} min{a.walkIn && " · walk-in"}
                    </p>
                    {view === "day" && s.can("clinical:write") && (a.status === "scheduled" || a.status === "arrived") && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {a.status === "scheduled" && (
                          <Button variant="secondary" disabled={busy === a.appointmentId} onClick={() => void act(a.appointmentId, () => api.appointments.checkIn(a.appointmentId, { version: a.version }))}>
                            Check in
                          </Button>
                        )}
                        {a.encounterId ? (
                          <Button variant="secondary" href={`/encounters/${a.encounterId}`}>
                            Open encounter
                          </Button>
                        ) : (
                          <Button variant="secondary" disabled={busy === a.appointmentId} onClick={() => void act(a.appointmentId, async () => navigate(`/encounters/${(await api.encounters.create({ patientId: a.patientId, locationId: a.locationId, kind: a.kind, appointmentId: a.appointmentId })).encounter.encounterId}`))}>
                            Start
                          </Button>
                        )}
                        <Button variant="secondary" onClick={() => setEditing(a)}>
                          Reschedule
                        </Button>
                        <CancelAppointment busy={busy === a.appointmentId} onCancel={(reason) => void act(a.appointmentId, () => api.appointments.cancel(a.appointmentId, { version: a.version, reason }))} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/** Slot starts within the location's hours (Q-18). */
function slotOptions(schedule: { opensAt: string; closesAt: string; slotMinutes: number }): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let m = minutesOf(schedule.opensAt); m <= minutesOf(schedule.closesAt); m += schedule.slotMinutes) {
    const h = Math.floor(m / 60);
    out.push({ value: hhmm(m), label: `${((h + 11) % 12) + 1}:${String(m % 60).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}` });
  }
  return out;
}

function AppointmentForm({ date, onClose, onSaved }: { date: string; onClose: () => void; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const location = s.location!;
  const slots = useMemo(() => slotOptions(location.schedule), [location.schedule]);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<PatientSummary[]>([]);
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [form, setForm] = useState({ date, time: slots[0]?.value ?? "08:00", kind: "test" as (typeof KINDS)[number], durationMinutes: location.schedule.defaultDurationMinutes });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      api.patients
        .search({ query: q, limit: 8 })
        .then((res) => setMatches(res.items))
        .catch(() => setMatches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [api, query]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!patient) return;
    setBusy(true);
    setError(null);
    try {
      await api.appointments.create({ patientId: patient.patientId, locationId: location.locationId, kind: form.kind, scheduledAt: zonedToIso(form.date, minutesOf(form.time), location.timezone), durationMinutes: form.durationMinutes });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="new-appointment" className="mb-6">
      <h2 id="new-appointment" className="mb-4 text-h3 font-semibold text-anchor">
        New appointment
      </h2>
      <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => void save(e)}>
        <div className="flex flex-col gap-2 md:col-span-2">
          {patient ? (
            <p>
              {fullName(patient)} · born {patient.dateOfBirth}{" "}
              <Button variant="secondary" onClick={() => setPatient(null)}>
                Change
              </Button>
            </p>
          ) : (
            <>
              <Field label="Patient" wide placeholder="Search by name or date of birth" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
              {matches.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {matches.map((m) => (
                    <li key={m.patientId}>
                      <Button variant="secondary" onClick={() => setPatient(m)}>
                        {fullName(m)} · {m.dateOfBirth}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <Field label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required hint={location.schedule.closedWeekdays.includes(isoWeekday(form.date)) ? "The location is closed that day; the appointment can still be saved." : undefined} />
        <Select label="Time" options={slots} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <Select label="Visit" options={KINDS.map((k) => ({ value: k, label: kindLabel(k) }))} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as (typeof KINDS)[number] })} />
        <Field label="Duration (minutes)" type="number" min={5} max={480} step={5} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
        {error && <Status kind="error" label={error} className="md:col-span-2" />}
        <div className="flex gap-2 md:col-span-2">
          <Button type="submit" disabled={busy || !patient}>
            Schedule
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </form>
    </Card>
  );
}

function RescheduleForm({ appointment, onClose, onSaved }: { appointment: Appointment; onClose: () => void; onSaved: () => void }) {
  const s = useSession();
  const api = useClinical();
  const location = s.location!;
  const slots = useMemo(() => slotOptions(location.schedule), [location.schedule]);
  const [form, setForm] = useState({ date: localDate(appointment.scheduledAt, location.timezone), time: hhmm(localMinutes(appointment.scheduledAt, location.timezone)), kind: appointment.kind, durationMinutes: appointment.durationMinutes });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeOptions = slots.some((o) => o.value === form.time) ? slots : [{ value: form.time, label: form.time }, ...slots];

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.appointments.reschedule(appointment.appointmentId, { version: appointment.version, scheduledAt: zonedToIso(form.date, minutesOf(form.time), location.timezone), kind: form.kind, durationMinutes: form.durationMinutes });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="reschedule" className="mb-6">
      <h2 id="reschedule" className="mb-1 text-h3 font-semibold text-anchor">
        Reschedule
      </h2>
      <p className="mb-4 text-ink-secondary">
        {fullName(appointment.patient)} · {kindLabel(appointment.kind)} · currently {formatCalendarDate(localDate(appointment.scheduledAt, location.timezone))} {formatTime(appointment.scheduledAt, location.timezone)}
      </p>
      <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => void save(e)}>
        <Field label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        <Select label="Time" options={timeOptions} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <Select label="Visit" options={KINDS.map((k) => ({ value: k, label: kindLabel(k) }))} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as (typeof KINDS)[number] })} />
        <Field label="Duration (minutes)" type="number" min={5} max={480} step={5} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
        {error && <Status kind="error" label={error} className="md:col-span-2" />}
        <div className="flex gap-2 md:col-span-2">
          <Button type="submit" disabled={busy}>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </form>
    </Card>
  );
}

function CancelAppointment({ busy, onCancel }: { busy: boolean; onCancel: (reason: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open)
    return (
      <Button variant="secondary" disabled={busy} onClick={() => setOpen(true)}>
        Cancel
      </Button>
    );
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onCancel(reason.trim() || null);
        setOpen(false);
      }}
    >
      <Field label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
      <Button type="submit" variant="secondary" disabled={busy}>
        Confirm cancel
      </Button>
      <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
        Keep
      </Button>
    </form>
  );
}
